import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ProductTranslationCompleteness } from '@today/types'
import type { TranslationDraftView } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProductDTO, TranslationDTO } from '@/services/product.service'
import { productService } from '@/services/product.service'
import {
	draftFromTranslation,
	draftToBody,
	isDirty,
	orderTabs,
	type TranslationDraft,
	translationErrors,
} from './product-translations'
import { productErrorMessage, translateKeys } from './product-ui'

/** The translations + completeness query shared by the tabs and the lifecycle bar. */
export const translationsKey = (id: string) => ['product-translations', id]

/** Load a product's translations and completeness. */
export function useTranslationsQuery(productId: string) {
	return useQuery({
		queryKey: translationsKey(productId),
		queryFn: () => productService.translations(productId),
		// Every mutation invalidates this explicitly; a second observer must not refetch on mount.
		staleTime: 30_000,
	})
}

type Dialog = 'discard' | 'delete' | null

/**
 * Tab selection with the unsaved-edits guard (REQ-006). A locale with unsaved
 * edits stays in the tab set even when the base set (tenant locales, product
 * default, stored translations) no longer contains it, so a dirty tab is never
 * dropped without an explicit Discard.
 */
function useTabGuard(baseTabs: string[], stored: Map<string, TranslationDTO>) {
	const [selected, setSelected] = useState<string | null>(null)
	const [pending, setPending] = useState<string | null>(null)
	const [drafts, setDrafts] = useState<Record<string, TranslationDraft>>({})
	const draftOf = (locale: string) => drafts[locale] ?? draftFromTranslation(stored.get(locale))
	const unsaved = Object.keys(drafts)
		.filter((locale) => !baseTabs.includes(locale) && isDirty(draftOf(locale), stored.get(locale)))
		.sort()
	const tabs = [...baseTabs, ...unsaved]
	const current = selected !== null && tabs.includes(selected) ? selected : (tabs[0] ?? '')
	const reset = (locale: string) =>
		setDrafts((all) => Object.fromEntries(Object.entries(all).filter(([key]) => key !== locale)))
	return {
		tabs,
		current,
		draftOf,
		setDrafts,
		reset,
		pending,
		request: (next: string) => {
			if (next === current) return
			if (isDirty(draftOf(current), stored.get(current))) setPending(next)
			else setSelected(next)
		},
		stay: () => setPending(null),
		discard: () => {
			reset(current)
			if (pending) setSelected(pending)
			setPending(null)
		},
	}
}

/**
 * Save/delete of a locale; both reload translations + completeness on success.
 * The target locale travels as the mutation's variables, so a late response acts
 * on the locale it was sent for, never on the tab selected when it resolves.
 */
function useTranslationMutations(
	productId: string,
	handlers: {
		onSaved: (locale: string, body: Record<string, unknown>) => void
		onDeleted: (locale: string) => void
		onSaveError: (e: unknown) => void
		onDeleteError: (e: unknown) => void
	},
) {
	const client = useQueryClient()
	const refresh = () => client.invalidateQueries({ queryKey: translationsKey(productId) })
	const save = useMutation({
		mutationFn: (target: { locale: string; body: Record<string, unknown> }) =>
			productService.saveTranslation(productId, target.locale, target.body),
		onSuccess: async (_saved, target) => {
			handlers.onSaved(target.locale, target.body)
			await refresh()
		},
		onError: handlers.onSaveError,
	})
	const remove = useMutation({
		mutationFn: (locale: string) => productService.deleteTranslation(productId, locale),
		onSuccess: async (_none, locale) => {
			handlers.onDeleted(locale)
			await refresh()
		},
		onError: handlers.onDeleteError,
	})
	return { save, remove }
}

const tabViews = (
	tabs: string[],
	completeness: ProductTranslationCompleteness | undefined,
	stored: Map<string, TranslationDTO>,
) =>
	tabs.map((locale) => ({
		locale,
		required: completeness?.requiredLocales.includes(locale) ?? false,
		missing: completeness?.missingLocales.includes(locale) ?? false,
		stored: stored.has(locale),
	}))

/** The stored translations, the ordered tab set and the guarded selection with drafts. */
function useTranslationTabs(product: ProductDTO, enabledLocales: string[]) {
	const query = useTranslationsQuery(product.productId)
	const stored = new Map((query.data?.data ?? []).map((tr) => [tr.locale, tr]))
	const guard = useTabGuard(orderTabs(enabledLocales, product.defaultLocale, [...stored.keys()]), stored)
	return { tabs: tabViews(guard.tabs, query.data?.completeness, stored), guard, draftOf: guard.draftOf }
}

interface Feedback {
	errors: Record<string, string>
	formError: string | null
	dialog: Dialog
	dialogError: string | null
}

const NO_FEEDBACK: Feedback = { errors: {}, formError: null, dialog: null, dialogError: null }

/**
 * Locale tabs of the product editor (TO-416 product-admin-ui REQ-006): the tab
 * order, per-locale drafts, save/delete, and the discard guard on tab switches.
 */
export function useProductTranslations(
	product: ProductDTO,
	enabledLocales: string[],
	caps: { write: boolean; delete: boolean },
) {
	const { t, i18n } = useTranslation('products')
	const { tabs, guard, draftOf } = useTranslationTabs(product, enabledLocales)
	const [feedback, setFeedback] = useState<Feedback>(NO_FEEDBACK)
	const set = (next: Partial<Feedback>) => setFeedback((f) => ({ ...f, ...next }))
	const message = (error: unknown) => productErrorMessage(error, t, (key) => i18n.exists(key, { ns: 'products' }))
	const locale = guard.current
	const { save, remove } = useTranslationMutations(product.productId, {
		// Text typed into the same tab after the save was sent stays as a draft.
		onSaved: (saved, body) => {
			if (JSON.stringify(draftToBody(draftOf(saved))) === JSON.stringify(body)) guard.reset(saved)
		},
		onDeleted: (deleted) => {
			guard.reset(deleted)
			set({ dialog: null })
		},
		onSaveError: (error) => set({ formError: message(error) }),
		onDeleteError: (error) => set({ dialogError: message(error) }),
	})

	return {
		t,
		tabs,
		selected: locale,
		draft: draftOf(locale) as TranslationDraftView,
		...feedback,
		readOnly: !caps.write,
		canDelete: caps.delete,
		isBusy: save.isPending || remove.isPending,
		dialog: guard.pending ? ('discard' as const) : feedback.dialog,
		onSelect: (next: string) => {
			set({ errors: {}, formError: null })
			guard.request(next)
		},
		onChange: (field: keyof TranslationDraftView, value: string) =>
			guard.setDrafts((all) => ({ ...all, [locale]: { ...draftOf(locale), [field]: value } })),
		onSave: () => {
			const found = translationErrors(draftOf(locale))
			set({ errors: translateKeys(found, t), formError: null })
			if (Object.keys(found).length === 0) save.mutate({ locale, body: draftToBody(draftOf(locale)) })
		},
		onDelete: () => set({ dialog: 'delete', dialogError: null }),
		onConfirmDelete: () => remove.mutate(locale),
		onCancelDelete: () => set({ dialog: null }),
		onStay: guard.stay,
		onDiscard: guard.discard,
	}
}
